import { useState } from "react";
import { createFileRoute, Outlet, redirect, useMatches, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/dashboard-layout";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { ivrQueryOptions, deleteIVRFn } from "@/server/ivr";
import { toast } from "sonner";
import { CrudEntry } from "@/lib/mma/types.gen";
import { ContextUser, checkIfUserMayWrite, checkIfUserMayDelete, getPermissionRestraintMessage } from "@/lib/utils";
import { usePermissionRedirect } from "@/hooks/use-permission-redirect";

export const Route = createFileRoute("/_dashboard/ivr")({
  component: IVRRouteComponent,
  loader: async ({ context }) => {
    return {
      crumb: "IVR",
      userProfileId: (context as { user?: { profile: string } }).user?.profile,
      accessControl: (context as { user?: { accessControl: Record<string, CrudEntry> } }).user?.accessControl,
      user: context.user as ContextUser | null | undefined,
    };
  },
});

function IVRRouteComponent() {
  const { userProfileId, accessControl, user } = Route.useLoaderData();
  usePermissionRedirect({ user, permission: "IVR" });
  //If the user has no profile, it means they have access to everything. Profiles are restrictive only.
  const mayWrite = checkIfUserMayWrite(userProfileId, accessControl, "IVR");
  const mayDelete = checkIfUserMayDelete(userProfileId, accessControl, "IVR");
  const ivrQuery = useQuery(ivrQueryOptions);
  const ivrs = ivrQuery.data ?? [];
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const matches = useMatches();
  const isRootIVR = matches[matches.length - 1].fullPath === "/ivr";

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedIVR, setSelectedIVR] = useState<{ id: string; name: string } | null>(null);
  const [loading, setLoading] = useState(false);

  function handleDeleteClick(ivrId: string, ivrName: string) {
    setSelectedIVR({ id: ivrId, name: ivrName });
    setDialogOpen(true);
  }

  async function handleConfirmDelete() {
    if (!selectedIVR) return;
    setLoading(true);
    toast.loading("Deleting IVR...", { id: "delete-ivr" });
    try {
      await deleteIVRFn({ data: { ivrId: selectedIVR.id } });
      // Invalidate and refetch the IVR list
      await queryClient.invalidateQueries({ queryKey: ["ivr"] });
      ivrQuery.refetch();
      toast.success(`IVR "${selectedIVR.name}" has been deleted successfully`, { id: "delete-ivr" });
      const currentIvrId = matches.find((m) => (m.params as any)?.ivrId)?.params as any;
      if (!currentIvrId?.ivrId || String(currentIvrId.ivrId) === String(selectedIVR.id)) {
        navigate({ to: "/ivr" });
      }
    } catch (error) {
      console.error("Failed to delete IVR:", error);
      toast.error("Failed to delete IVR. Please try again.", { id: "delete-ivr" });
    } finally {
      setLoading(false);
      setDialogOpen(false);
      setSelectedIVR(null);
    }
  }

  return (
    <>
      <DashboardLayout
        submenu={{
          items: ivrs,
          isLoading: ivrQuery.isLoading,
          onClick: (item: any, functionType: string) => {
            if (functionType === "delete") {
              if (!mayDelete) {
                toast.error(getPermissionRestraintMessage("delete IVRs"));
                return;
              }
              handleDeleteClick(item.parent.id, item.parent.name);
            }
          },
          title: "IVR",
          active: true,
          createNew: mayWrite ? [{ label: "+ Create new IVR", href: "/ivr/new-ivr" }] : undefined,
        }}
      >
        {isRootIVR ? (
          <Card>
            <CardContent>
              <p className="text-md pb-20 pt-15 text-center">
                Please select an IVR system from the submenu to view its details or manage it.
              </p>
            </CardContent>
          </Card>
        ) : (
          <Outlet />
        )}
      </DashboardLayout>
      <ConfirmDialog
        open={dialogOpen}
        title="Delete IVR"
        description={selectedIVR ? `Are you sure you want to delete the IVR "${selectedIVR.name}"?` : ""}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDialogOpen(false);
          setSelectedIVR(null);
        }}
        loading={loading}
      />
    </>
  );
}
